import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Rocket, ClipboardCheck, MessageCircle } from "lucide-react";
import { track } from "@/lib/analytics";

const SHOWN_KEY = "funnel_exit_intent_shown";

/**
 * Exit-Intent Modal — shows once per session when the user moves their cursor
 * out of the top edge of the viewport (desktop), or hits the back button (mobile).
 *
 * Props:
 *   - lead: { affiliate_url, whatsapp_url, campaign_id, lead_id }
 *   - source: identifier for analytics ("guide1", "guide2", etc.)
 */
export default function ExitIntentModal({ lead, source }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Show once per session across all funnel steps
    if (sessionStorage.getItem(SHOWN_KEY)) return;

    let armed = true;
    const triggerOnce = () => {
      if (!armed) return;
      armed = false;
      try { sessionStorage.setItem(SHOWN_KEY, "1"); } catch (_) { /* noop */ }
      setOpen(true);
      track("funnel_exit_intent_shown", {
        campaignId: lead?.campaign_id,
        leadId: lead?.lead_id,
        payload: { source },
      });
    };

    // Desktop: cursor leaves through top of viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && (!e.relatedTarget || e.relatedTarget.nodeName === "HTML")) {
        triggerOnce();
      }
    };
    // Mobile: back button — push a fake state, intercept popstate
    const onPopState = () => {
      if (armed) {
        triggerOnce();
        // Re-push so user doesn't actually leave on the first back press
        window.history.pushState({ exitIntent: true }, "");
      }
    };
    // Mobile fallback: tab becomes hidden (switching tabs / closing)
    const onVisibility = () => {
      if (document.visibilityState === "hidden") triggerOnce();
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("popstate", onPopState);
    document.addEventListener("visibilitychange", onVisibility);
    // Seed a dummy history state for popstate to fire on first back press
    window.history.pushState({ exitIntent: true }, "");

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => setOpen(false);

  const onAffiliateClick = () => {
    track("funnel_affiliate_click", {
      campaignId: lead?.campaign_id,
      leadId: lead?.lead_id,
      payload: { source: `exit_intent_${source}` },
    });
  };

  const onWhatsappClick = () => {
    track("funnel_whatsapp_click", {
      campaignId: lead?.campaign_id,
      leadId: lead?.lead_id,
      payload: { source: `exit_intent_${source}` },
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={close}
          data-testid="exit-intent-modal"
          dir="rtl"
        >
          <motion.div
            initial={{ scale: 0.9, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-[28px] shadow-2xl overflow-hidden w-full max-w-[440px]"
          >
            {/* Orange header with rocket + target rings */}
            <div className="relative bg-brand-amber text-white px-7 pt-7 pb-9 overflow-hidden">
              {/* Decorative bullseye rings (top-right) */}
              <div className="absolute -top-10 -right-10 w-56 h-56 pointer-events-none" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="absolute rounded-full border-[3px] border-white/15"
                    style={{
                      inset: `${i * 12}px`,
                    }}
                  />
                ))}
              </div>
              {/* Close button (top-left in RTL) */}
              <button
                type="button"
                onClick={close}
                className="absolute top-4 left-4 w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center transition"
                aria-label="סגירה"
                data-testid="exit-intent-close"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {/* Rocket icon centered */}
              <div className="relative flex justify-center mb-3">
                <Rocket className="w-12 h-12 text-white drop-shadow-md" strokeWidth={2.2} />
              </div>

              {/* Title + subtitle */}
              <h2 className="relative text-center text-[28px] sm:text-[32px] leading-[1.15] font-[800] mb-3">
                רגע לפני שאתם הולכים...
              </h2>
              <p className="relative text-center text-white/95 text-[15px] leading-[1.55] px-2">
                אתם במרחק של טופס אחד מאוויר לנשימה ומאלפי שקלים שחוזרים אליכם בכל חודש.
              </p>
            </div>

            {/* White body */}
            <div className="px-7 pt-7 pb-7">
              <p className="text-center text-stone-700 leading-[1.7] mb-6 text-[15px]">
                בואו נבדוק האם הדירה שלכם יכולה להציל אתכם מהריביות. זה לוקח 2 דקות ודיסקרטי לחלוטין.
              </p>

              {lead?.affiliate_url ? (
                <a
                  href={lead.affiliate_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={onAffiliateClick}
                  data-testid="exit-intent-cta"
                  className="inline-flex items-center justify-center gap-2.5 w-full h-14 rounded-full bg-brand-charcoal hover:bg-stone-900 text-white font-[800] text-[16px] transition active:scale-[0.98] pulse-cta"
                >
                  <ClipboardCheck className="w-5 h-5" />
                  לבדיקת זכאות בחינם
                </a>
              ) : null}

              {lead?.whatsapp_url ? (
                <a
                  href={lead.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={onWhatsappClick}
                  data-testid="exit-intent-whatsapp"
                  className="mt-3 inline-flex items-center justify-center gap-2.5 w-full h-14 rounded-full bg-white border-2 border-stone-200 hover:border-stone-300 text-brand-charcoal font-[800] text-[15px] transition active:scale-[0.98]"
                >
                  <MessageCircle className="w-5 h-5 text-emerald-500" fill="currentColor" />
                  להצטרפות לקהילת הווצאפ
                </a>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
